import {
  EyeInvisibleOutlined,
  EyeOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  AssignableType,
  TaskAssignment,
  TaskEvent,
  TaskEventType,
  TaskableType,
  TaskableTypeEnum,
} from '@hefe/models';
import { TasksApi, userActiveBasketballCoachTeamId } from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import {
  Button,
  Col,
  Divider,
  Empty,
  Modal,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import CustomTable from '../../../core/components/table/custom-table.component';
import TaskAssignmentSelectComponent from '../task-assignment-select/task-assignment-select.component';
import TasksAutoAssignmentWizard from '../tasks-auto-assignment-wizard/tasks-auto-assignment-wizard/tasks-auto-assignment-wizard.component';
import SubscriptionWrapper from '../../../core/components/subscription-wrapper/subscription-wrapper.component';
import { AuthSubscriptions } from '@hefe/auth';

interface TaskAssignmentComponentProps {
  taskId?: string;
  type: TaskableTypeEnum;
}

export default function TaskAssignmentComponent(
  props: TaskAssignmentComponentProps
) {
  const { taskId } = props;

  const [showPastEvents, setShowPastEvents] = useState(false);
  const [automaticAssignmentWizardOpen, setAutomaticAssignmentWizardOpen] =
    useState(false);

  const [getEvents, { isFetching, data }] =
    TasksApi.useLazyFetchTaskEventsQuery();

  const teamId = useSelector(userActiveBasketballCoachTeamId);

  useEffect(() => {
    fetchEvents();
  }, [taskId, props.type]);

  const fetchEvents = () => {
    if (!taskId || (props.type === TaskableType.BASKETBALL_TEAM && !teamId)) {
      return;
    }

    const idSplit = taskId.split('_');
    if (idSplit.length !== 2) {
      return;
    }

    const id = parseInt(taskId.split('_')[1]);

    if (props.type === TaskableType.CLUB) {
      getEvents({ taskId: id });
    } else if (props.type === TaskableType.BASKETBALL_TEAM && teamId) {
      getEvents({ taskId: id, teamId });
    }
  };

  const events = useMemo(() => {
    if (!data?.events) {
      return [];
    }

    return data.events.filter((event) => {
      const isPastEvent = dayjs(event.date).isBefore(dayjs());

      return showPastEvents ? true : !isPastEvent;
    });
  }, [data?.events, showPastEvents]);

  /**
   * Tasks infos
   */
  const taskInformation = useMemo(() => {
    if (!data?.task) {
      return null;
    }

    const { task } = data;

    const where = [];
    if (task.at_home_games) {
      where.push(t('at_home_games'));
    }
    if (task.at_away_games) {
      where.push(t('at_away_games'));
    }
    if (task.at_practices) {
      where.push(t('at_practices'));
    }

    const minUsers = task.min_num_of_users ? task.min_num_of_users : 0;
    const maxUsers = task.max_num_of_users ? task.max_num_of_users : '∞';

    return (
      <Row>
        <Col xs={24}>
          <Typography.Title level={4}>{task.name}</Typography.Title>
          <Space direction="vertical" size={0}>
            {task.description && task.description.length > 0 && (
              <Typography.Text>{task.description}</Typography.Text>
            )}
            <Typography.Text>{where.join(', ')}</Typography.Text>
            <Typography.Text>
              {minUsers} - {maxUsers} {t('users.users')}
            </Typography.Text>
          </Space>
        </Col>
      </Row>
    );
  }, [data]);

  if (!taskId) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div>
      {taskInformation}
      <Divider />
      <Row>
        <CustomTable
          name="team-task-assignment"
          toolbarItems={[
            <SubscriptionWrapper
              subscription={AuthSubscriptions.third}
              title={t('tasks.auto_assignment.title')}
            >
              <Tooltip title={t(`tasks.auto_assignment.title`)}>
                <Button
                  onClick={() =>
                    setAutomaticAssignmentWizardOpen((oldValue) => !oldValue)
                  }
                  icon={<RobotOutlined />}
                />
              </Tooltip>
            </SubscriptionWrapper>,
            <Tooltip
              title={t(`tasks.past_events.${showPastEvents ? 'hide' : 'show'}`)}
            >
              <Button
                onClick={() => setShowPastEvents((oldValue) => !oldValue)}
                icon={
                  showPastEvents ? <EyeInvisibleOutlined /> : <EyeOutlined />
                }
              />
            </Tooltip>,
          ]}
          warningRowKeys={events
            ?.filter(
              (event) =>
                data?.task?.min_num_of_users &&
                (event?.num_of_user_assignments ?? 0) <
                  data?.task?.min_num_of_users
            )
            .map((event) => event.id)}
          columns={[
            {
              key: 'date',
              title: t('date'),
              dataIndex: 'date',
              default: true,
              render: (value: string) => formatDateTime(value),
            },
            {
              key: 'description',
              title: t('description'),
              dataIndex: 'description',
              default: true,
              render: (value: string, event: TaskEvent) =>
                event.type === TaskEventType.BASKETBALL_GAME
                  ? value
                  : t('practice'),
            },
            {
              key: 'location',
              title: t('location'),
              dataIndex: 'location',
              default: true,
            },
            ...(data?.task?.taskable_type === TaskableType.CLUB
              ? [
                  {
                    key: 'num_of_user_assignments',
                    title: (
                      <Typography.Text>
                        {t('tasks.num_of_user_assignments')}
                        <Tooltip
                          title={t('tasks.num_of_user_assignments_more')}
                        >
                          <FaInfoCircle style={{ marginLeft: 5 }} />
                        </Tooltip>
                      </Typography.Text>
                    ),
                    dataIndex: 'num_of_user_assignments',
                    default: true,
                  },
                ]
              : []),
            ...(props.type === TaskableType.CLUB
              ? [
                  {
                    key: 'team_assignments',
                    title: t('tasks.teams_assignments'),
                    dataIndex: 'assignments',
                    default: true,
                    render: (value: TaskAssignment[], item: TaskEvent) => (
                      <TaskAssignmentSelectComponent
                        task={
                          data?.task ?? {
                            id: 0,
                            name: '',
                            taskable_id: 0,
                            taskable_type: props.type,
                          }
                        }
                        taskId={taskId}
                        assignments={value.filter(
                          (a) =>
                            a.assignable_type === AssignableType.BASKETBALL_TEAM
                        )}
                        assignableType={AssignableType.BASKETBALL_TEAM}
                        event={item}
                        type={props.type}
                      />
                    ),
                  },
                  {
                    key: 'user_assignments',
                    title: t('tasks.users_assignments'),
                    dataIndex: 'assignments',
                    default: true,
                    render: (value: TaskAssignment[], item: TaskEvent) => (
                      <TaskAssignmentSelectComponent
                        task={
                          data?.task ?? {
                            id: 0,
                            name: '',
                            taskable_id: 0,
                            taskable_type: props.type,
                          }
                        }
                        taskId={taskId}
                        assignments={value.filter(
                          (a) => a.assignable_type === AssignableType.USER
                        )}
                        assignableType={AssignableType.USER}
                        event={item}
                        type={props.type}
                      />
                    ),
                  },
                ]
              : [
                  {
                    key: 'assignments',
                    title: t('tasks.assignment'),
                    dataIndex: 'assignments',
                    default: true,
                    render: (value: TaskAssignment[], item: TaskEvent) => (
                      <TaskAssignmentSelectComponent
                        task={
                          data?.task ?? {
                            id: 0,
                            name: '',
                            taskable_id: 0,
                            taskable_type: props.type,
                          }
                        }
                        taskId={taskId}
                        assignments={value}
                        assignableType={AssignableType.ALL}
                        event={item}
                        type={props.type}
                      />
                    ),
                  },
                ]),
          ]}
          rowKey={'id'}
          dataSource={events}
          loading={isFetching}
        />
      </Row>
      <Modal
        open={automaticAssignmentWizardOpen}
        width={'80%'}
        footer={null}
        onCancel={() => setAutomaticAssignmentWizardOpen(false)}
      >
        {data?.task && events && (
          <TasksAutoAssignmentWizard
            task={data?.task}
            events={events}
            closeModal={() => setAutomaticAssignmentWizardOpen(false)}
            type={props.type}
          />
        )}
      </Modal>
    </div>
  );
}
